import apiClient from './index';
import type { Product, SearchParams, SearchType } from '../types/product';

export async function searchProductsWithParams(params: SearchParams): Promise<Product[]> {
  const requestBody: Record<string, unknown> = {
    [params.type]: params.query,
    page_size: params.page_size || 10,
    include_alternatives: params.include_alternatives ?? true,
  };

  const response = await apiClient.post<Product[]>('/spex/plus/search', requestBody);
  return response.data;
}

export async function searchProducts(
  type: SearchType,
  query: string,
  pageSize = 20,
  exactMatch?: boolean,
  external?: boolean
): Promise<Product[]> {
  const requestBody: Record<string, unknown> = {
    [type]: query,
    page_size: pageSize,
    include_alternatives: true,
  };

  if (type === 'mpn' && exactMatch !== undefined) {
    requestBody.exact_match = exactMatch;
  }
  // Opt into a live external-provider lookup (the "Search online" action).
  // Omitted by default so normal search stays local-first.
  if (external) {
    requestBody.external = true;
  }

  const response = await apiClient.post<Product[]>('/spex/plus/search', requestBody);
  return response.data;
}

export async function getProductById(
  productId: string,
  includeAlternatives = true
): Promise<Product | null> {
  const response = await apiClient.post<Product[]>('/spex/plus/search', {
    product_id: productId,
    include_alternatives: includeAlternatives,
  });
  return response.data[0] || null;
}

export async function getProductByMpn(
  mpn: string,
  includeAlternatives = true
): Promise<Product | null> {
  const response = await apiClient.post<Product[]>('/spex/plus/search', {
    mpn: mpn,
    include_alternatives: includeAlternatives,
  });
  return response.data[0] || null;
}

export async function searchByType(
  type: SearchType,
  query: string,
  pageSize = 10
): Promise<Product[]> {
  return searchProducts(type, query, pageSize);
}

// Filter type for source dropdown (includes 'all' for "no filter")
export type DataSourceFilter = 'all' | 'etilize' | 'hp' | 'external' | 'icecat' | 'pim';

export async function listProducts(pageSize = 20, source: DataSourceFilter = 'all'): Promise<Product[]> {
  const response = await apiClient.get<Product[]>('/spex/plus/products', {
    params: { page_size: pageSize, source },
  });
  return response.data;
}

export async function getManufacturersAutocomplete(query?: string, limit = 50): Promise<string[]> {
  const response = await apiClient.get<string[]>('/spex/plus/autocomplete/manufacturers', {
    params: { q: query || '', limit },
  });
  return response.data;
}

export interface SourceContribution {
  source_name: string;
  pim_product_id: number;
  product_id: string;
  product_summary: {
    mfgpartno: string | null;
    manufacturer: string | null;
    description: string | null;
  };
  identifiers_contributed: { type: string; value: string }[];
  request_history: {
    timestamp: string;
    method: string;
    endpoint: string;
    request_params: Record<string, unknown> | null;
    response_status: number;
    response_time_ms: number | null;
  }[];
}

export async function getSourceContributions(
  pimProductId: number,
  sourceName: string
): Promise<SourceContribution> {
  const response = await apiClient.get<SourceContribution>(
    `/api/v1/products/pim/${pimProductId}/sources/${encodeURIComponent(sourceName)}`
  );
  return response.data;
}

export async function getCategoriesAutocomplete(query?: string, limit = 50): Promise<string[]> {
  const response = await apiClient.get<string[]>('/spex/plus/autocomplete/categories', {
    params: { q: query || '', limit },
  });
  return response.data;
}
